/* Keeps the selected gradient frames in phase (pf-scroll-pilot.css, pf-variant-frame-travel).
   The selected variant, add-on and Boost frames resume a paused lap when picked, so frames picked
   at different times drift apart. This seeks every frame in the pilot section to the page clock
   at boot, on each pick, and after a variant change rebuilds the option rows. Seeking leaves the
   play state with CSS (only play()/pause() take it over), so the paused/running switch and the
   fade still work. Reduced motion sets animation: none, so there is nothing to seek.
   The laps only run on screen: a row, add-on list, Boost offer or protection panel scrolled out
   of view gets data-pf-frame-idle, which pauses its frames (pf-scroll-pilot.css), and is seeked
   back to the clock when it returns. Angle animations repaint on the main thread every frame.
   Plain asset by design (no webpack). Document-level listeners survive the in-place product
   swaps that replace the section markup. */
(() => {
  "use strict";
  if (window.pfScrollPilotFrameClock) return;
  window.pfScrollPilotFrameClock = true;

  const SECTION = '[data-pf-experience="pf_scroll_pilot"]';
  const OPTION_ROW = ".pf-product-scroll__variant-option";
  // Add-on cards, the Boost claim button and the Boost "No thanks" button.
  const TOGGLE = "[data-pf-scroll-complementary-option], [data-pf-boost-offer-decline]";
  const ANIMATION = "pf-variant-frame-travel";
  const FALLBACK_LAP_MS = 4000;
  // Containers whose selected frames lap; pausing them off screen keeps an idle page idle.
  const FRAME_HOSTS = [
    OPTION_ROW,
    ".pf-scroll-complementary-selector",
    ".pf-pilot-boost-offer",
    ".pf-scroll-warranty-experiment",
    "[data-pf-pilot-trade-in]",
  ].join(", ");
  const IDLE = "data-pf-frame-idle";

  // getAnimations({ subtree: true }) is what returns the ::before/::after frame animations.
  const syncFrameClock = (root) => {
    if (typeof root?.getAnimations !== "function") return;
    const now = document.timeline?.currentTime ?? performance.now();
    root.getAnimations({ subtree: true }).forEach((animation) => {
      if (animation.animationName !== ANIMATION) return;
      const duration = Number(animation.effect?.getTiming?.().duration) || FALLBACK_LAP_MS;
      animation.currentTime = now % duration;
    });
  };
  const syncAll = () => document.querySelectorAll(SECTION).forEach(syncFrameClock);

  let visibility;
  const onVisibility = (entries) => {
    entries.forEach((entry) => {
      entry.target.toggleAttribute(IDLE, !entry.isIntersecting);
      if (entry.isIntersecting) syncFrameClock(entry.target);
    });
  };
  // Re-run after anything that replaces the markup (variant rebuilds, product swaps, the editor).
  const watchVisibility = () => {
    if (typeof window.IntersectionObserver !== "function") return;
    visibility?.disconnect();
    visibility = new IntersectionObserver(onVisibility, { rootMargin: "100px 0px" });
    document.querySelectorAll(SECTION).forEach((section) => {
      section.querySelectorAll(FRAME_HOSTS).forEach((host) => visibility.observe(host));
    });
  };
  const refresh = () => {
    syncAll();
    watchVisibility();
  };

  // A pick's selected/claimed state (and with it the running play state) can land a frame or two
  // after the event, and a paused lap resumes from wherever it was seeked, which left the claimed
  // Boost frame 30-80 ms behind. Seek again once the new state has painted.
  const syncSettled = (section) => {
    syncFrameClock(section);
    window.requestAnimationFrame?.(() =>
      window.requestAnimationFrame(() => syncFrameClock(section))
    );
    window.setTimeout(() => syncFrameClock(section), 250);
  };

  // A variant pick. Syncing the whole section also seeks the frame that was just deselected, so
  // it fades out at the same angle as the new one fades in.
  document.addEventListener("change", (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== "radio" || !input.checked) return;
    if (!input.closest(OPTION_ROW)) return;
    const section = input.closest(SECTION);
    if (section) syncSettled(section);
  });

  // An add-on or Boost toggle. pf-scroll-pilot-behavior.js loads first and toggles is-selected in
  // its own click handlers before this one runs.
  document.addEventListener("click", (event) => {
    const toggle = event.target?.closest?.(TOGGLE);
    const section = toggle?.closest(SECTION);
    if (section) syncSettled(section);
  });

  // A trade-in tap. The step redraws its card on the tap, so this listens before it does (capture)
  // while the target is still in the section, then seeks the redrawn frames once they paint.
  document.addEventListener(
    "click",
    (event) => {
      const section = event.target?.closest?.("[data-pf-pilot-trade-in]")?.closest(SECTION);
      if (section) syncSettled(section);
    },
    true
  );

  // Option rows rebuilt for the new variant start their frames from zero; realign them once the
  // rebuild has run.
  const subscribeToVariantChange = () => {
    if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") return;
    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      window.setTimeout(syncAll, 0);
      window.setTimeout(refresh, 400);
    });
  };

  const boot = () => {
    refresh();
    subscribeToVariantChange();
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  document.addEventListener("shopify:section:load", refresh);
})();
